/**
 * Feature slide carousel — synced content panels and image stack.
 * Media nodes are moved once into persistent layers (no cloning) so images
 * preload and transitions never re-trigger picture-breakpoint lazy loading.
 */
class FeatureSlideCarousel extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.isAnimating = false;
    this.mediaSources = [];
    /** @type {HTMLDivElement[]} */
    this.mediaLayers = [];
    this.panels = [];
    this.touchStartX = 0;
    /** @type {HTMLDivElement | null} */
    this.loopCloneLeading = null;
    /** @type {HTMLDivElement | null} */
    this.loopCloneTrailing = null;

    this._onPrevClick = this._onPrevClick.bind(this);
    this._onNextClick = this._onNextClick.bind(this);
    this._onPrevKeydown = this._onPrevKeydown.bind(this);
    this._onNextKeydown = this._onNextKeydown.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onViewportKeydown = this._onViewportKeydown.bind(this);
    this._onPeekClick = this._onPeekClick.bind(this);
  }

  connectedCallback() {
    this._unbind();

    this.loop = this.dataset.loop === 'true';
    this.slideCount = Number.parseInt(this.dataset.slideCount || '0', 10);

    this.panelsHost = this.querySelector('[data-feature-slide-panels]');
    this.track = this.querySelector('[data-feature-slide-track]');
    this.mediaStack = this.querySelector('[data-feature-slide-media-stack]');
    this.viewport = this.querySelector('[data-feature-slide-viewport]');
    this.prevButton = this.querySelector('[data-feature-slide-prev]');
    this.nextButton = this.querySelector('[data-feature-slide-next]');

    if (!this.panelsHost || !this.track || !this.mediaStack || this.slideCount < 1) return;

    this._collectSlides();
    if (!this.mediaSources.length) return;

    this._buildMediaLayers();

    this.prevButton?.addEventListener('click', this._onPrevClick);
    this.nextButton?.addEventListener('click', this._onNextClick);
    this.prevButton?.addEventListener('keydown', this._onPrevKeydown);
    this.nextButton?.addEventListener('keydown', this._onNextKeydown);
    this.viewport?.addEventListener('keydown', this._onViewportKeydown);
    this.viewport?.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.viewport?.addEventListener('touchend', this._onTouchEnd, { passive: true });
    this.mediaStack.addEventListener('click', this._onPeekClick);
    window.addEventListener('resize', this._onResize);

    this._applyLayout(0, false);
    this._setActivePanel(0, false);
    this._updateNavState();
  }

  disconnectedCallback() {
    this._unbind();
    this.loopCloneLeading = null;
    this.loopCloneTrailing = null;
  }

  _unbind() {
    this.prevButton?.removeEventListener('click', this._onPrevClick);
    this.nextButton?.removeEventListener('click', this._onNextClick);
    this.prevButton?.removeEventListener('keydown', this._onPrevKeydown);
    this.nextButton?.removeEventListener('keydown', this._onNextKeydown);
    this.viewport?.removeEventListener('keydown', this._onViewportKeydown);
    this.viewport?.removeEventListener('touchstart', this._onTouchStart);
    this.viewport?.removeEventListener('touchend', this._onTouchEnd);
    this.mediaStack?.removeEventListener('click', this._onPeekClick);
    window.removeEventListener('resize', this._onResize);
  }

  /**
   * Collect content panels and media sources from rendered blocks.
   */
  _collectSlides() {
    const items = Array.from(this.track.querySelectorAll('[data-feature-slide-item]'));

    items.forEach((item, index) => {
      const content = item.querySelector('[data-feature-slide-content]');
      const media = item.querySelector('[data-feature-slide-media]');

      if (content) {
        content.classList.add('feature-slide-carousel__panel');
        content.dataset.slideIndex = String(index);
        this.panelsHost.appendChild(content);
        this.panels.push(content);
      }

      if (media) {
        this.mediaSources.push(media);
      }
    });
  }

  /**
   * Move each slide media into a persistent layer and preload all images.
   */
  _buildMediaLayers() {
    this.mediaLayers = [];
    this.mediaStack.replaceChildren();

    this.mediaSources.forEach((media, index) => {
      const layer = document.createElement('div');
      layer.className = 'feature-slide-carousel__layer';
      layer.dataset.slideIndex = String(index);
      layer.appendChild(media);

      this.mediaLayers.push(layer);
    });

    this._preloadMedia();
  }

  /**
   * Eagerly load all slide images while layers are connected to the DOM.
   */
  _preloadMedia() {
    const fragment = document.createDocumentFragment();
    this.mediaLayers.forEach((layer) => fragment.appendChild(layer));
    this.mediaStack.appendChild(fragment);

    this.mediaStack.querySelectorAll('picture-breakpoint').forEach((element) => {
      if (element.dataset.mounted !== 'true' && typeof element.loadImage === 'function') {
        element.loadImage();
      }
    });
  }

  /**
   * @param {number} index
   * @returns {HTMLDivElement | undefined}
   */
  _getLayer(index) {
    return this.mediaLayers[index];
  }

  /**
   * @returns {boolean}
   */
  _isDesktop() {
    return window.matchMedia('(min-width: 1280px)').matches;
  }

  /**
   * @param {number} direction
   * @returns {number}
   */
  _getTargetIndex(direction) {
    const nextIndex = this.activeIndex + direction;

    if (this.loop) {
      return (nextIndex + this.slideCount) % this.slideCount;
    }

    return Math.min(Math.max(nextIndex, 0), this.slideCount - 1);
  }

  /**
   * @param {number} direction
   * @returns {boolean}
   */
  _canNavigate(direction) {
    if (this.slideCount <= 1 || this.isAnimating) return false;
    if (this.loop) return true;
    return this._getTargetIndex(direction) !== this.activeIndex;
  }

  /**
   * @param {Element | null} button
   * @param {boolean} isDisabled
   */
  _setNavDisabled(button, isDisabled) {
    if (!button) return;
    button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    button.classList.toggle('swiper-button-disabled', isDisabled);
  }

  _updateNavState() {
    if (this.slideCount <= 1) return;

    if (this.loop) {
      this._setNavDisabled(this.prevButton, false);
      this._setNavDisabled(this.nextButton, false);
      return;
    }

    this._setNavDisabled(this.prevButton, this.activeIndex <= 0);
    this._setNavDisabled(this.nextButton, this.activeIndex >= this.slideCount - 1);
  }

  /**
   * @param {number} index
   * @param {boolean} animate
   */
  _setActivePanel(index, animate) {
    const previousIndex = this.activeIndex;

    if (!animate) {
      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;
        panel.classList.remove('is-transitioning-in', 'is-transitioning-out');
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
      return;
    }

    const previousPanel = this.panels[previousIndex];
    const nextPanel = this.panels[index];

    if (previousPanel && previousPanel !== nextPanel) {
      previousPanel.hidden = false;
      previousPanel.classList.add('is-transitioning-out');
      previousPanel.classList.remove('is-active');
    }

    if (nextPanel) {
      nextPanel.hidden = false;
      nextPanel.classList.add('is-transitioning-in', 'is-active');
    }

    window.setTimeout(() => {
      previousPanel?.classList.remove('is-transitioning-out');
      nextPanel?.classList.remove('is-transitioning-in');
      this._setActivePanel(index, false);
    }, this._getTransitionDuration());
  }

  /**
   * @returns {number}
   */
  _getTransitionDuration() {
    const styles = window.getComputedStyle(this);
    const rawValue = styles.getPropertyValue('--feature-slide-carousel-transition-duration').trim();
    const parsed = Number.parseInt(rawValue, 10);
    return Number.isFinite(parsed) ? parsed : 500;
  }

  /**
   * Desktop slide width + gap in pixels.
   * @returns {number}
   */
  _getSlideStepPx() {
    const row = this.mediaStack?.querySelector('.feature-slide-carousel__row');
    const layer = row?.firstElementChild;

    if (row && layer instanceof HTMLElement && layer.offsetWidth > 0) {
      const rowStyles = window.getComputedStyle(row);
      const gap =
        Number.parseFloat(rowStyles.columnGap) || Number.parseFloat(rowStyles.gap) || 0;

      return layer.offsetWidth + gap;
    }

    const styles = window.getComputedStyle(this);
    const width = Number.parseFloat(
      styles.getPropertyValue('--feature-slide-carousel-media-width-pc').trim()
    );
    const gap = Number.parseFloat(
      styles.getPropertyValue('--feature-slide-carousel-media-gap-pc').trim()
    );

    return (Number.isFinite(width) ? width : 590) + (Number.isFinite(gap) ? gap : 24);
  }

  /**
   * @param {...(HTMLDivElement | undefined)} layers
   */
  _resetLayerState(...layers) {
    const stateClasses = [
      'feature-slide-carousel__layer--current',
      'feature-slide-carousel__layer--next',
      'feature-slide-carousel__layer--prev',
      'feature-slide-carousel__layer--peek',
      'feature-slide-carousel__layer--far',
      'is-animating-forward',
      'is-animating-backward',
      'is-entering-forward',
      'is-entering-backward',
      'is-exiting-forward',
      'is-exiting-backward',
    ];

    layers.forEach((layer) => {
      if (!layer) return;
      layer.classList.remove(...stateClasses);
      layer.style.transform = '';
      layer.hidden = false;
      layer.removeAttribute('data-peek-direction');
    });
  }

  /**
   * Measure the active slide media height for mobile animated transitions.
   */
  _syncMeasuredMediaSize() {
    const media = this.mediaStack?.querySelector(
      '.feature-slide-carousel__layer--current .feature-slide-item__media, .feature-slide-carousel__layer .feature-slide-item__media'
    );

    if (!media) return;

    const width = media.offsetWidth;
    const height = media.offsetHeight;

    if (width > 0) {
      this.mediaStack.style.setProperty('--feature-slide-carousel-measured-width', `${width}px`);
    }

    if (height > 0) {
      this.mediaStack.style.setProperty('--feature-slide-carousel-measured-height', `${height}px`);
    }
  }

  /**
   * @returns {boolean}
   */
  _usesLoopClones() {
    return this.loop && this.slideCount > 1 && this._isDesktop();
  }

  /**
   * Map logical slide index to row position (leading clone shifts real slides by +1).
   * @param {number} activeIndex
   * @returns {number}
   */
  _getVisualIndex(activeIndex) {
    if (this._usesLoopClones()) return activeIndex + 1;
    return activeIndex;
  }

  /**
   * Create persistent clone layers for seamless loop transitions.
   */
  _ensureLoopClones() {
    if (!this._usesLoopClones()) return;

    const firstLayer = this.mediaLayers[0];
    const lastLayer = this.mediaLayers[this.slideCount - 1];

    if (!firstLayer || !lastLayer) return;

    if (!this.loopCloneLeading) {
      this.loopCloneLeading = this._createCloneLayer(lastLayer, 'leading');
    }

    if (!this.loopCloneTrailing) {
      this.loopCloneTrailing = this._createCloneLayer(firstLayer, 'trailing');
    }
  }

  /**
   * @param {HTMLDivElement} sourceLayer
   * @param {'leading' | 'trailing'} position
   * @returns {HTMLDivElement}
   */
  _createCloneLayer(sourceLayer, position) {
    const clone = /** @type {HTMLDivElement} */ (sourceLayer.cloneNode(true));
    clone.classList.add('feature-slide-carousel__layer--clone');
    clone.dataset.clonePosition = position;
    clone.dataset.slideIndex = sourceLayer.dataset.slideIndex || '';
    clone.removeAttribute('data-peek-direction');
    return clone;
  }

  /**
   * @param {HTMLDivElement | null | undefined} layer
   * @param {boolean} isCurrent
   */
  _setLayerOpacityRole(layer, isCurrent) {
    if (!layer) return;

    layer.classList.toggle('feature-slide-carousel__layer--current', isCurrent);
    layer.classList.toggle('feature-slide-carousel__layer--peek', !isCurrent);
    layer.classList.toggle('feature-slide-carousel__layer--far', !isCurrent);
    layer.classList.remove('feature-slide-carousel__layer--next');
    layer.removeAttribute('data-peek-direction');
  }

  /**
   * Crossfade opacity between outgoing and incoming layers during row animation.
   * @param {HTMLDivElement | null | undefined} outgoing
   * @param {HTMLDivElement | null | undefined} incoming
   */
  _crossfadeLayers(outgoing, incoming) {
    if (outgoing && incoming && outgoing !== incoming) {
      this._setLayerOpacityRole(outgoing, false);
      incoming.classList.remove(
        'feature-slide-carousel__layer--peek',
        'feature-slide-carousel__layer--far',
        'feature-slide-carousel__layer--next'
      );
      incoming.classList.add('feature-slide-carousel__layer--current');
      incoming.removeAttribute('data-peek-direction');
    }
  }

  /**
   * @param {number} activeIndex
   * @returns {boolean}
   */
  _isImmediateNextIndex(activeIndex, index) {
    if (index === activeIndex) return false;

    if (this.loop) {
      return index === (activeIndex + 1) % this.slideCount;
    }

    return index === activeIndex + 1;
  }

  /**
   * Update layer roles (current / peek / next) for a given active index.
   * @param {number} activeIndex
   * @param {HTMLDivElement | null} [row]
   */
  _applyLayerRoles(activeIndex, row = null) {
    this.mediaLayers.forEach((layer, index) => {
      const isCurrent = index === activeIndex;
      const isImmediateNext = this._isImmediateNextIndex(activeIndex, index);

      layer.classList.toggle('feature-slide-carousel__layer--current', isCurrent);
      layer.classList.toggle('feature-slide-carousel__layer--peek', !isCurrent);
      layer.classList.toggle('feature-slide-carousel__layer--next', isImmediateNext);
      layer.classList.toggle('feature-slide-carousel__layer--far', !isCurrent && !isImmediateNext);

      if (isImmediateNext) {
        layer.dataset.peekDirection = '1';
      } else {
        layer.removeAttribute('data-peek-direction');
      }
    });

    if (!this._usesLoopClones()) return;

    const rowEl = row || this.mediaStack?.querySelector('.feature-slide-carousel__row');
    if (!rowEl) return;

    const leading = rowEl.querySelector('[data-clone-position="leading"]');
    const trailing = rowEl.querySelector('[data-clone-position="trailing"]');

    if (leading) {
      leading.classList.remove(
        'feature-slide-carousel__layer--current',
        'feature-slide-carousel__layer--next'
      );
      leading.classList.add('feature-slide-carousel__layer--peek', 'feature-slide-carousel__layer--far');
      leading.removeAttribute('data-peek-direction');
    }

    if (trailing) {
      const isImmediateNext = activeIndex === this.slideCount - 1;

      trailing.classList.remove('feature-slide-carousel__layer--current');
      trailing.classList.add('feature-slide-carousel__layer--peek');
      trailing.classList.toggle('feature-slide-carousel__layer--next', isImmediateNext);
      trailing.classList.toggle('feature-slide-carousel__layer--far', !isImmediateNext);

      if (isImmediateNext) {
        trailing.dataset.peekDirection = '1';
      } else {
        trailing.removeAttribute('data-peek-direction');
      }
    }
  }

  /**
   * Build a desktop row containing all slide layers in order.
   * @returns {HTMLDivElement}
   */
  _buildDesktopRow() {
    const row = document.createElement('div');
    row.className = 'feature-slide-carousel__row';

    if (this._usesLoopClones()) {
      this._ensureLoopClones();
      if (this.loopCloneLeading) row.appendChild(this.loopCloneLeading);
    }

    this.mediaLayers.forEach((layer) => {
      row.appendChild(layer);
    });

    if (this._usesLoopClones() && this.loopCloneTrailing) {
      row.appendChild(this.loopCloneTrailing);
    }

    return row;
  }

  /**
   * @param {HTMLDivElement} row
   * @param {number} visualIndex
   * @param {boolean} animate
   */
  _setRowOffset(row, visualIndex, animate = false) {
    const offset = visualIndex * this._getSlideStepPx();
    row.classList.toggle('is-transitioning', animate);
    row.style.transform = `translateX(-${offset}px)`;
  }

  /**
   * Whether desktop row can slide by one step (including loop wrap).
   * @param {number} fromIndex
   * @param {number} toIndex
   * @returns {boolean}
   */
  _canAnimateDesktopRow(fromIndex, toIndex) {
    if (toIndex === fromIndex) return false;
    if (!this.loop) return Math.abs(toIndex - fromIndex) === 1;

    const forwardStep = (fromIndex + 1) % this.slideCount;
    const backwardStep = (fromIndex - 1 + this.slideCount) % this.slideCount;

    return toIndex === forwardStep || toIndex === backwardStep;
  }

  /**
   * @param {number} fromIndex
   * @param {number} targetIndex
   * @returns {{ visualFrom: number, visualTo: number, isWrapForward: boolean, isWrapBackward: boolean }}
   */
  _getDesktopAnimationTargets(fromIndex, targetIndex) {
    const isWrapForward =
      this._usesLoopClones() && fromIndex === this.slideCount - 1 && targetIndex === 0;
    const isWrapBackward =
      this._usesLoopClones() && fromIndex === 0 && targetIndex === this.slideCount - 1;

    const visualFrom = this._getVisualIndex(fromIndex);
    let visualTo = this._getVisualIndex(targetIndex);

    if (isWrapForward) {
      visualTo = this.slideCount + 1;
    } else if (isWrapBackward) {
      visualTo = 0;
    }

    return { visualFrom, visualTo, isWrapForward, isWrapBackward };
  }

  /**
   * @param {HTMLDivElement} row
   * @param {number} fromIndex
   * @param {number} targetIndex
   * @param {boolean} isWrapForward
   * @param {boolean} isWrapBackward
   * @returns {{ outgoing: HTMLDivElement | null | undefined, incoming: HTMLDivElement | null | undefined }}
   */
  _getAnimationLayers(row, fromIndex, targetIndex, isWrapForward, isWrapBackward) {
    const outgoing = this._getLayer(fromIndex);

    if (isWrapForward) {
      return {
        outgoing,
        incoming: row.querySelector('[data-clone-position="trailing"]'),
      };
    }

    if (isWrapBackward) {
      return {
        outgoing,
        incoming: row.querySelector('[data-clone-position="leading"]'),
      };
    }

    return {
      outgoing,
      incoming: this._getLayer(targetIndex),
    };
  }

  /**
   * Slide the full desktop row left/right with opacity crossfade.
   * @param {number} targetIndex
   */
  _animateDesktopRow(targetIndex) {
    const fromIndex = this.activeIndex;
    const row = this._buildDesktopRow();
    const { visualFrom, visualTo, isWrapForward, isWrapBackward } = this._getDesktopAnimationTargets(
      fromIndex,
      targetIndex
    );

    this._resetLayerState(...this.mediaLayers);
    this.mediaStack.replaceChildren();
    this.mediaStack.classList.add('feature-slide-carousel__stack--static');
    this.mediaStack.appendChild(row);

    this._applyLayerRoles(fromIndex, row);
    this._syncMeasuredMediaSize();
    this._setRowOffset(row, visualFrom, false);

    const { outgoing, incoming } = this._getAnimationLayers(
      row,
      fromIndex,
      targetIndex,
      isWrapForward,
      isWrapBackward
    );
    this._crossfadeLayers(outgoing, incoming);

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this._setRowOffset(row, visualTo, true);
      });
    });

    const duration = this._getTransitionDuration();

    window.setTimeout(() => {
      this.activeIndex = targetIndex;
      this.isAnimating = false;
      row.classList.remove('is-transitioning');
      this._applyLayerRoles(targetIndex, row);
      this._setRowOffset(row, this._getVisualIndex(targetIndex), false);
      this._updateNavState();
    }, duration);
  }

  /**
   * @param {number} direction 0 = static, 1 = forward, -1 = backward
   * @param {boolean} animate
   */
  _applyLayout(direction = 0, animate = false) {
    if (!this.mediaStack || !this.mediaLayers.length) return;

    const isDesktop = this._isDesktop();

    this._resetLayerState(...this.mediaLayers);
    this.mediaStack.replaceChildren();
    this.mediaStack.classList.remove(
      'feature-slide-carousel__stack--static',
      'feature-slide-carousel__stack--animated',
      'feature-slide-carousel__stack--mobile'
    );

    if (this.slideCount <= 1) {
      const layer = this._getLayer(this.activeIndex);
      if (!layer) return;
      layer.classList.add('feature-slide-carousel__layer--current');
      this.mediaStack.appendChild(layer);
      return;
    }

    if (animate && direction !== 0 && !isDesktop) {
      const targetIndex = this._getTargetIndex(direction);
      const currentLayer = this._getLayer(this.activeIndex);
      const incomingLayer = this._getLayer(targetIndex);

      if (!currentLayer || !incomingLayer) return;

      this.mediaStack.classList.add('feature-slide-carousel__stack--animated');
      this.mediaStack.classList.add('feature-slide-carousel__stack--mobile');

      currentLayer.classList.add('feature-slide-carousel__layer--current');
      incomingLayer.classList.add(
        direction === 1
          ? 'feature-slide-carousel__layer--next'
          : 'feature-slide-carousel__layer--prev'
      );

      this.mediaStack.append(currentLayer, incomingLayer);
      this._syncMeasuredMediaSize();

      const animClass = direction === 1 ? 'is-animating-forward' : 'is-animating-backward';
      const enterClass = direction === 1 ? 'is-entering-forward' : 'is-entering-backward';
      const exitClass = direction === 1 ? 'is-exiting-forward' : 'is-exiting-backward';

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this._syncMeasuredMediaSize();
          currentLayer.classList.add(animClass, exitClass);
          incomingLayer.classList.add(animClass, enterClass);
        });
      });
      return;
    }

    this.mediaStack.classList.add('feature-slide-carousel__stack--static');

    if (isDesktop) {
      const row = this._buildDesktopRow();
      this.mediaStack.appendChild(row);
      this._applyLayerRoles(this.activeIndex, row);
      this._syncMeasuredMediaSize();
      this._setRowOffset(row, this._getVisualIndex(this.activeIndex), false);
      return;
    }

    this.mediaStack.classList.add('feature-slide-carousel__stack--mobile');

    const currentLayer = this._getLayer(this.activeIndex);
    if (!currentLayer) return;

    currentLayer.classList.add('feature-slide-carousel__layer--current');
    this.mediaStack.appendChild(currentLayer);
  }

  /**
   * @param {number} direction
   */
  _go(direction) {
    if (!this._canNavigate(direction)) return;

    const targetIndex = this._getTargetIndex(direction);
    if (targetIndex === this.activeIndex) return;

    const shouldAnimate = this.slideCount > 1;
    const isDesktop = this._isDesktop();
    const canSlideRow = isDesktop && this._canAnimateDesktopRow(this.activeIndex, targetIndex);

    this.isAnimating = true;
    this._setActivePanel(targetIndex, shouldAnimate);

    if (shouldAnimate && canSlideRow) {
      this._animateDesktopRow(targetIndex);
      return;
    }

    if (shouldAnimate && !isDesktop) {
      this._applyLayout(direction, true);
      window.setTimeout(() => {
        this.activeIndex = targetIndex;
        this.isAnimating = false;
        this._applyLayout(0, false);
        this._updateNavState();
      }, this._getTransitionDuration());
      return;
    }

    this.activeIndex = targetIndex;
    this._applyLayout(0, false);
    this.isAnimating = false;
    this._updateNavState();
  }

  _onPrevClick() {
    this._handleNavActivate(this.prevButton, -1);
  }

  _onNextClick() {
    this._handleNavActivate(this.nextButton, 1);
  }

  /**
   * @param {KeyboardEvent} event
   */
  _onPrevKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this._handleNavActivate(this.prevButton, -1);
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  _onNextKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this._handleNavActivate(this.nextButton, 1);
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  _onViewportKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this._handleNavActivate(this.prevButton, -1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this._handleNavActivate(this.nextButton, 1);
    }
  }

  /**
   * @param {Element | null} button
   * @param {number} direction
   */
  _handleNavActivate(button, direction) {
    if (button && button.getAttribute('aria-disabled') === 'true') return;
    this._go(direction);
  }

  _onResize() {
    if (this.isAnimating) return;
    this._applyLayout(0, false);
  }

  /**
   * @param {TouchEvent} event
   */
  _onTouchStart(event) {
    this.touchStartX = event.changedTouches[0]?.clientX ?? 0;
  }

  /**
   * @param {TouchEvent} event
   */
  _onTouchEnd(event) {
    if (this._isDesktop()) return;

    const touchEndX = event.changedTouches[0]?.clientX ?? 0;
    const deltaX = touchEndX - this.touchStartX;
    const threshold = 48;

    if (deltaX <= -threshold) {
      this._go(1);
      return;
    }

    if (deltaX >= threshold) {
      this._go(-1);
    }
  }

  /**
   * @param {MouseEvent} event
   */
  _onPeekClick(event) {
    if (!this._isDesktop() || this.isAnimating) return;

    const peekLayer = event.target.closest('[data-peek-direction="1"]');
    if (!peekLayer) return;

    this._go(1);
  }
}

if (!customElements.get('feature-slide-carousel')) {
  customElements.define('feature-slide-carousel', FeatureSlideCarousel);
}
